import proj4 from 'proj4';
import { cleanDescription } from './signUtils';
import { type Regulation, type Schedule } from '../shared/types';

// NY State Plane East Zone (NAD83)
export const nySpcs = '+proj=lcc +lat_1=41.03333333333333 +lat_2=40.66666666666666 +lat_0=40.16666666666666 +lon_0=-74 +x_0=300000.0000000001 +y_0=0 +ellps=GRS80 +datum=NAD83 +units=us-ft +no_defs';
export const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';

export const SOCRATA_BASE_URL = 'https://data.cityofnewyork.us/resource/nfid-uabd.json';

export const DAY_MAP: { [key: string]: string } = {
  'MONDAY': 'MO',
  'TUESDAY': 'TU',
  'WEDNESDAY': 'WE',
  'THURSDAY': 'TH',
  'FRIDAY': 'FR',
  'SATURDAY': 'SA',
  'SUNDAY': 'SU'
};

interface SocrataResponse {
  order_number: string;
  record_type: string;
  borough: string;
  on_street: string;
  from_street: string;
  to_street: string;
  side_of_street: string;
  sign_description: string;
  sign_x_coord: string;
  sign_y_coord: string;
}

// Caches
const streetNormalizationCache = new Map<string, string>();
const geocodeCache = new Map<string, google.maps.GeocoderResult[]>();
// Maps borough -> (normalizedName -> originalName)
const boroughStreetsCache = new Map<string, Map<string, string>>();
// Per-borough TTL timestamps
const boroughCacheTimestamps = new Map<string, number>();

// Cache configuration
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Fuzzy matching configuration
const SIMILARITY_THRESHOLD = 0.8; // 80% similarity required

// Helper function to calculate Levenshtein distance (O(n) space)
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  const curr = new Array(n + 1);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      curr[j] = str1[i - 1] === str2[j - 1]
        ? prev[j - 1]
        : Math.min(prev[j - 1] + 1, prev[j] + 1, curr[j - 1] + 1);
    }
    [prev, ] = [curr, prev];
  }

  return prev[n];
}

// Calculate similarity ratio between strings
function calculateSimilarity(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length);
  const distance = levenshteinDistance(str1, str2);
  return 1 - distance / maxLength;
}

function normalizeStreetName(street: string): string {
  // Check cache first
  const cached = streetNormalizationCache.get(street);
  if (cached) return cached;

  // Convert to uppercase and trim
  let normalized = street.toUpperCase().trim();

  // Enhanced normalization rules
  normalized = normalized
    // Remove ordinal suffixes
    .replace(/(\d+)(ST|ND|RD|TH)/, '$1')
    // Standardize directions
    .replace(/^(N|S|E|W)\s+/, (match) => {
      const dirs = { N: 'NORTH', S: 'SOUTH', E: 'EAST', W: 'WEST' };
      return `${dirs[match[0] as keyof typeof dirs]} `;
    })
    // For numbered streets with directions
    .replace(/^(EAST|WEST|NORTH|SOUTH)\s+(\d+)/, (_, dir, num) => `${dir}  ${num} STREET`)
    // Standardize street types
    .replace(/\b(ST|STREET|STR)\b/, 'STREET')
    .replace(/\b(AVE|AVENUE|AV)\b/, 'AVENUE')
    .replace(/\b(RD|ROAD)\b/, 'ROAD')
    .replace(/\b(BLVD|BOULEVARD)\b/, 'BOULEVARD')
    // Remove special characters
    .replace(/[^\w\s]/g, ' ')
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    .trim();

  // Cache the result
  streetNormalizationCache.set(street, normalized);
  return normalized;
}

async function updateBoroughStreetsCache(borough: string): Promise<void> {
  try {
    const query = {
      'borough': borough,
      '$select': 'DISTINCT on_street',
      '$limit': '5000'
    };
    const queryString = new URLSearchParams(query).toString();
    const response = await fetch(`${SOCRATA_BASE_URL}?${queryString}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const streets: { on_street: string }[] = await response.json();
    const streetMap = new Map<string, string>();
    for (const s of streets) {
      streetMap.set(normalizeStreetName(s.on_street), s.on_street);
    }

    boroughStreetsCache.set(borough, streetMap);
    boroughCacheTimestamps.set(borough, Date.now());
  } catch (error) {
    console.error('Error updating borough streets cache:', error);
    throw error;
  }
}

async function findClosestStreetMatch(
  normalizedStreet: string,
  borough: string
): Promise<string | null> {
  try {
    // Check if this borough's cache needs updating
    const lastUpdate = boroughCacheTimestamps.get(borough) ?? 0;
    if (Date.now() - lastUpdate > CACHE_TTL || !boroughStreetsCache.has(borough)) {
      await updateBoroughStreetsCache(borough);
    }

    const streetMap = boroughStreetsCache.get(borough);
    if (!streetMap) {
      throw new Error(`No streets found for borough: ${borough}`);
    }

    // Find the best match using the cached streets
    let bestNormalized: string | null = null;
    let highestSimilarity = 0;

    for (const normalizedKey of streetMap.keys()) {
      const similarity = calculateSimilarity(normalizedStreet, normalizedKey);

      if (similarity > highestSimilarity && similarity >= SIMILARITY_THRESHOLD) {
        highestSimilarity = similarity;
        bestNormalized = normalizedKey;
      }

      // Early exit if we find an exact match
      if (similarity === 1) {
        break;
      }
    }

    // Return the original (un-normalized) street name directly from cache -- no extra fetch needed
    return bestNormalized ? (streetMap.get(bestNormalized) ?? null) : null;
  } catch (error) {
    console.error('Error in findClosestStreetMatch:', error);
    return null;
  }
}

async function determineBorough(
  addressComponents: google.maps.GeocoderAddressComponent[]
): Promise<string | null> {
  const boroughComponent = addressComponents.find(component => 
    component.types.includes('sublocality_level_1') ||
    component.types.includes('sublocality') ||
    (component.types.includes('political') && 
     ['Bronx', 'Brooklyn', 'Queens', 'Manhattan', 'Staten Island'].includes(component.long_name))
  );

  if (boroughComponent) {
    const boroughMap: { [key: string]: string } = {
      'Manhattan': 'Manhattan',
      'Brooklyn': 'Brooklyn',
      'Queens': 'Queens',
      'Bronx': 'Bronx',
      'The Bronx': 'Bronx',
      'Staten Island': 'Staten Island'
    };
    return boroughMap[boroughComponent.long_name] || null;
  }

  // Check for Manhattan via county
  const county = addressComponents.find(component =>
    component.types.includes('administrative_area_level_2')
  );
  if (county && county.long_name === 'New York County') {
    return 'Manhattan';
  }

  return null;
}

export function parseSignDescription(description: string): Schedule {
  const dayRegex = new RegExp(Object.keys(DAY_MAP).join('|'), 'gi');
  const days = (description.match(dayRegex) || []).map(day => DAY_MAP[day.toUpperCase()]);

  // Time parsing logic
  let startTime = null;
  let endTime = null;

  // Try parsing with various formats
  let timeMatch;

  // Format: 9AM to 10:30AM or 9 AM to 10:30 AM
  timeMatch = description.match(/(\d{1,2})\s*(?:AM|PM)\s*(?:to|-)\s*(\d{1,2}):(\d{2})\s*(?:AM|PM)/i);
  if (timeMatch) {
    startTime = `${timeMatch[1].padStart(2, '0')}:00`;
    endTime = `${timeMatch[2].padStart(2, '0')}:${timeMatch[3]}`;
  }

  // Format: 9:30AM to 10:30AM
  if (!timeMatch) {
    timeMatch = description.match(/(\d{1,2}):(\d{2})\s*(?:AM|PM)\s*(?:to|-)\s*(\d{1,2}):(\d{2})\s*(?:AM|PM)/i);
    if (timeMatch) {
      startTime = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
      endTime = `${timeMatch[3].padStart(2, '0')}:${timeMatch[4]}`;
    }
  }

  // Format: 9:30AM to 10AM
  if (!timeMatch) {
    timeMatch = description.match(/(\d{1,2}):(\d{2})\s*(?:AM|PM)\s*(?:to|-)\s*(\d{1,2})\s*(?:AM|PM)/i);
    if (timeMatch) {
      startTime = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
      endTime = `${timeMatch[3].padStart(2, '0')}:00`;
    }
  }

  // Format: 9AM to 10AM
  if (!timeMatch) {
    timeMatch = description.match(/(\d{1,2})\s*(?:AM|PM)\s*(?:to|-)\s*(\d{1,2})\s*(?:AM|PM)/i);
    if (timeMatch) {
      startTime = `${timeMatch[1].padStart(2, '0')}:00`;
      endTime = `${timeMatch[2].padStart(2, '0')}:00`;
    }
  }

  return {
    days,
    startTime,
    endTime,
    raw: description
  };
}

function processRegulations(regulations: SocrataResponse[]): Regulation[] {
  // Group regulations by street segment for faster processing
  const segmentMap = new Map<string, SocrataResponse[]>();

  for (const reg of regulations) {
    const key = `${reg.on_street}|${reg.from_street}|${reg.to_street}|${reg.side_of_street}`;
    const segment = segmentMap.get(key) || [];
    segment.push(reg);
    segmentMap.set(key, segment);
  }

  const processed: Regulation[] = [];

  // Process each segment
  for (const [, segmentRegs] of segmentMap) {
    // Use the first regulation's coordinates for the segment
    const firstReg = segmentRegs[0];
    const x = parseFloat(firstReg.sign_x_coord);
    const y = parseFloat(firstReg.sign_y_coord);

    if (!isFinite(x) || !isFinite(y)) continue;

    // Process all schedules for the segment
    for (const reg of segmentRegs) {
      const schedule = parseSignDescription(reg.sign_description);

      if (schedule.days.length === 0 || !schedule.startTime || !schedule.endTime) {
        continue;
      }

      processed.push({
        id: reg.order_number,
        street: reg.on_street,
        fromStreet: reg.from_street,
        toStreet: reg.to_street,
        side: reg.side_of_street,
        schedule,
        coordinates: { x, y }
      });
    }
  }

  return processed;
}

function selectFinalRegulations(
  regulations: Regulation[],
  geocoded: { lat: number; lng: number; }
): Regulation[] {
  // Add distance to each regulation
  const regulationsWithDistance = regulations.map(reg => {
    // Convert from NY State Plane to WGS84
    const [regLng, regLat] = proj4(nySpcs, wgs84, [reg.coordinates.x, reg.coordinates.y]);

    // Compute distance from user geocoded location
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(geocoded.lat, geocoded.lng),
      new google.maps.LatLng(regLat, regLng)
    );

    return {
      ...reg,
      distance
    };
  });

  // Sort by distance
  const sortedByDistance = regulationsWithDistance.sort((a, b) => a.distance - b.distance);

  // Get closest 6 regulations
  const closestRegulations = sortedByDistance.slice(0, 6);

  // Select up to 2 unique schedule combinations
  const usedCombos = new Set<string>();
  const finalRegulations: Regulation[] = [];

  // Try from closest 6 first
  for (const reg of closestRegulations) {
    const scheduleKey = `${reg.schedule.days.join(',')}-${reg.schedule.startTime}-${reg.schedule.endTime}`;
    if (!usedCombos.has(scheduleKey)) {
      usedCombos.add(scheduleKey);
      finalRegulations.push(reg);

      if (finalRegulations.length === 2) break;
    }
  }

  // If we don't have 2 yet, try all remaining regulations
  if (finalRegulations.length < 2) {
    for (const reg of sortedByDistance) {
      if (finalRegulations.length === 2) break;

      const scheduleKey = `${reg.schedule.days.join(',')}-${reg.schedule.startTime}-${reg.schedule.endTime}`;
      if (!usedCombos.has(scheduleKey)) {
        usedCombos.add(scheduleKey);
        finalRegulations.push(reg);
      }
    }
  }

  return finalRegulations;
}

export async function queryParkingRegulations({ 
  street, 
  borough,
  geocoded
}: { 
  street: string; 
  borough: string;
  geocoded: { lat: number; lng: number; }
}): Promise<Regulation[]> {
  try {
    console.log('Querying for:', { street, borough });

    // Normalize the street name
    const normalizedStreet = normalizeStreetName(street);
    console.log('Normalized street:', normalizedStreet);

    // Get the corrected borough using cached geocoding when possible
    const cacheKey = `${geocoded.lat},${geocoded.lng}`;
    let geocodeResult: google.maps.GeocoderResult[];

    if (geocodeCache.has(cacheKey)) {
      geocodeResult = geocodeCache.get(cacheKey)!;
    } else {
      const geocoder = new google.maps.Geocoder();
      geocodeResult = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({
          location: { lat: geocoded.lat, lng: geocoded.lng }
        }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
      geocodeCache.set(cacheKey, geocodeResult);
    }

    // Get corrected borough
    const correctedBorough = await determineBorough(geocodeResult[0].address_components);
    if (!correctedBorough) {
      throw new Error(`Could not determine borough for coordinates: ${geocoded.lat}, ${geocoded.lng}`);
    }

    console.log('Corrected borough:', correctedBorough);

    // Try exact match first
    let query = {
      'on_street': normalizedStreet,
      'borough': correctedBorough,
      '$limit': '1000',
      'record_type': 'Current'
    };

    let queryString = new URLSearchParams(query).toString();
    let response = await fetch(`${SOCRATA_BASE_URL}?${queryString}`);
    let data: SocrataResponse[] = await response.json();

    // If no results, try fuzzy matching
    if (data.length === 0) {
      console.log('No exact matches found, trying fuzzy matching...');
      const fuzzyMatch = await findClosestStreetMatch(normalizedStreet, correctedBorough);

      if (fuzzyMatch) {
        console.log('Found fuzzy match:', fuzzyMatch);
        query.on_street = fuzzyMatch;
        queryString = new URLSearchParams(query).toString();
        response = await fetch(`${SOCRATA_BASE_URL}?${queryString}`);
        data = await response.json();
      }
    }

    console.log('Raw data count:', data.length);

    // Clean and filter regulations
    const cleaningRegulations = data
      .map(reg => ({
        ...reg,
        sign_description: cleanDescription(reg.sign_description)
      }))
      .filter(reg => 
        reg.sign_description.includes('BROOM SYMBOL') ||
        reg.sign_description.includes('(SANITATION SYMBOL')
      );

    console.log('Cleaning regulations found:', cleaningRegulations.length);

    if (cleaningRegulations.length === 0) {
      throw new Error(`No street cleaning regulations found for ${street} in ${correctedBorough}`);
    }

    // Process regulations
    const regulations = processRegulations(cleaningRegulations);

    // Select final regulations based on proximity and uniqueness
    const finalRegulations = selectFinalRegulations(regulations, geocoded);

    console.log('Final regulations selected:', finalRegulations.length);
    console.log('Selected regulations:', finalRegulations.map(reg => ({
      segment: `${reg.fromStreet} to ${reg.toStreet}`,
      side: reg.side,
      schedule: `${reg.schedule.days.join('/')} ${reg.schedule.startTime}-${reg.schedule.endTime}`
    })));

    return finalRegulations;

  } catch (error) {
    console.error('Error in queryParkingRegulations:', error);
    throw error;
  }
}